<template>
  <v-container class="py-4 budget-page" style="max-width: 1500px;">
    <template v-if="show_header">
      <div class="d-flex flex-wrap align-center mb-5 budget-header-gap">
        <div class="d-flex align-center flex-grow-1 min-w-0">
          <v-avatar color="primary" size="54" class="mr-4 elevation-2">
            <v-icon dark large>mdi-wallet-outline</v-icon>
          </v-avatar>
          <div class="min-w-0">
            <div class="text-h4 font-weight-bold primary--text" style="line-height: 1.1;">
              Presupuesto
            </div>
            <div class="text-body-2 grey--text text--darken-1 mt-1">
              Monitorea la ejecucion presupuestaria por unidad de negocio, location, departamento y cuenta.
            </div>
            <div class="d-flex flex-wrap align-center mt-2 context-chips-gap">
              <v-chip small label color="primary" text-color="white">
                <v-icon x-small left>mdi-domain</v-icon>
                {{ selectedBusinessName }}
              </v-chip>
              <v-chip small label outlined color="primary">
                <v-icon x-small left>mdi-calendar-range</v-icon>
                {{ selectedPeriodName }}
              </v-chip>
              <v-chip small label outlined :color="filters.location && filters.location.value !== 0 ? 'primary' : 'grey'">
                <v-icon x-small left>mdi-map-marker</v-icon>
                {{ selectedLocationName }}
              </v-chip>
            </div>
          </div>
        </div>

        <div class="d-flex flex-wrap align-center header-actions-gap">
          <v-btn-toggle v-model="viewMode" mandatory dense class="rounded-lg">
            <v-btn value="dashboard" small class="text-none px-4">
              <v-icon small left>mdi-view-dashboard-outline</v-icon>
              Dashboard
            </v-btn>
            <v-btn value="table" small class="text-none px-4">
              <v-icon small left>mdi-table</v-icon>
              Tabla
            </v-btn>
          </v-btn-toggle>

          <v-btn
            color="primary"
            outlined
            class="text-none rounded-lg"
            :loading="loading"
            @click="refreshData"
          >
            <v-icon left small>mdi-refresh</v-icon>
            Actualizar
          </v-btn>

          <v-btn
            v-if="hasFeature('SHOP-012')"
            color="success"
            depressed
            class="text-none font-weight-bold rounded-lg"
            @click="add"
          >
            <v-icon left>{{ new_budget.show ? 'mdi-content-save' : 'mdi-plus' }}</v-icon>
            {{ new_budget.show ? 'Guardar presupuesto' : 'Nuevo presupuesto' }}
          </v-btn>
        </div>
      </div>

      <v-card outlined class="rounded-lg mb-4 budget-filters-card overflow-hidden">
        <div class="filters-accent"></div>
        <v-card-text class="pa-4">
          <div class="d-flex flex-wrap align-center mb-3 filters-title-gap">
            <div class="d-flex align-center">
              <v-icon small color="grey darken-1" class="mr-2">mdi-filter-variant</v-icon>
              <span class="text-caption text-uppercase font-weight-bold grey--text text--darken-2 section-label">
                Filtros de analisis
              </span>
            </div>
            <v-spacer></v-spacer>
            <v-btn text small class="text-none" @click="resetAnalysisFilters">
              <v-icon x-small left>mdi-filter-remove-outline</v-icon>
              Limpiar refinamiento
            </v-btn>
          </div>

          <v-row dense>
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="filters.calendar"
                :items="calendars"
                item-text="name"
                item-value="id"
                return-object
                label="Calendario"
                prepend-inner-icon="mdi-calendar-range"
                outlined dense hide-details
                @change="handleCalendarChange"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="filters.period"
                :items="periods"
                item-text="display_name"
                item-value="id"
                return-object
                label="Periodo"
                prepend-inner-icon="mdi-calendar-clock"
                outlined dense hide-details
                @change="refreshData"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.range_label" class="text-caption">
                      {{ item.range_label }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
                <template v-slot:selection="{ item }">
                  <div class="d-flex flex-column py-1" style="line-height: 1.1;">
                    <span>{{ item.name }}</span>
                    <span v-if="item.range_label" class="text-caption grey--text text--darken-1">
                      {{ item.range_label }}
                    </span>
                  </div>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="filters.bussines_unit"
                :items="businessUnitsFromCalendar"
                item-text="description_long"
                item-value="id"
                return-object
                label="Unidad de negocio"
                prepend-inner-icon="mdi-domain"
                outlined dense hide-details
                @change="handleBusinessChange"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="filters.location"
                :items="businessLocations"
                item-text="text"
                item-value="value"
                return-object
                label="Location"
                prepend-inner-icon="mdi-map-marker"
                outlined dense hide-details
                @change="refreshData"
              ></v-autocomplete>
            </v-col>
          </v-row>

          <v-row dense class="mt-1">
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="filters.department"
                :items="departmentOptions"
                item-text="text"
                item-value="value"
                return-object
                label="Departamento"
                prepend-inner-icon="mdi-office-building-outline"
                outlined dense hide-details
                @change="find_budget"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="filters.accountingType"
                :items="accountingTypeOptions"
                item-text="name"
                item-value="id"
                return-object
                label="Tipo de cuenta"
                prepend-inner-icon="mdi-book-open-variant"
                outlined dense hide-details
                @change="handleAccountingTypeChange"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="filters.account"
                :items="accountOptions"
                item-text="name"
                item-value="id"
                return-object
                label="Cuenta"
                prepend-inner-icon="mdi-format-list-bulleted-type"
                outlined dense hide-details
                @change="find_budget"
              ></v-autocomplete>
            </v-col>
          </v-row>

          <v-row dense class="mt-1">
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="filters.category"
                :items="categoryOptions"
                item-text="name"
                item-value="id"
                return-object
                label="Categoria"
                prepend-inner-icon="mdi-folder-outline"
                outlined dense hide-details
                @change="handleCategoryChange"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="filters.subcategory"
                :items="subcategoryOptions"
                item-text="name"
                item-value="id"
                return-object
                label="Subcategoria"
                prepend-inner-icon="mdi-folder-multiple-outline"
                outlined dense hide-details
                @change="find_budget"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="search"
                label="Buscar en cuenta, categoria o departamento"
                prepend-inner-icon="mdi-magnify"
                clearable
                outlined dense hide-details
              ></v-text-field>
            </v-col>
          </v-row>

          <div class="mt-4">
            <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2 section-label mb-2">
              Vista rapida
            </div>
            <v-btn-toggle v-model="quickStatus" mandatory dense class="rounded-lg quick-toggle mb-2">
              <v-btn
                v-for="status in quickStatusOptions"
                :key="status.value"
                :value="status.value"
                small
                class="text-none px-4"
              >
                <v-icon x-small left>{{ status.icon }}</v-icon>
                {{ status.label }}
              </v-btn>
            </v-btn-toggle>

            <div v-if="activeFilterChips.length" class="d-flex flex-wrap context-chips-gap mt-2">
              <v-chip
                v-for="chip in activeFilterChips"
                :key="chip.key"
                small
                label
                outlined
                color="primary"
              >
                {{ chip.label }}
              </v-chip>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <v-expand-transition>
        <v-card v-show="new_budget.show" outlined class="rounded-lg mb-4 overflow-hidden">
          <div class="budget-new-header px-4 py-3 d-flex flex-wrap align-center new-budget-gap">
            <div class="d-flex align-center">
              <v-icon dark small class="mr-2">mdi-pencil-plus-outline</v-icon>
              <span class="white--text text-subtitle-2 font-weight-bold">Gestion del presupuesto seleccionado</span>
            </div>
            <v-spacer></v-spacer>
            <v-chip small label color="white" class="primary--text font-weight-bold">
              {{ budgetSelectionLabel }}
            </v-chip>
            <v-btn icon dark x-small @click="new_budget.show = false">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>

          <v-card-text class="pa-4">
            <v-row dense>
              <v-col cols="12" md="2">
                <div class="text-caption grey--text text--darken-1 mb-1">Ajuste (+/-)</div>
                <v-text-field
                  v-model="new_budget.amount"
                  label="Monto ajuste"
                  type="number"
                  :min="parseFloat(new_budget.remaining_amount || 0) * -1"
                  prepend-inner-icon="mdi-plus-minus"
                  dense outlined hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <div class="text-caption grey--text text--darken-1 mb-1">Nuevo Total</div>
                <v-text-field
                  v-model="totalAmountProxy"
                  label="Monto total"
                  type="number"
                  prepend-inner-icon="mdi-currency-usd"
                  dense outlined hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="1">
                <div class="text-caption grey--text text--darken-1 mb-1">Actual</div>
                <v-sheet rounded class="pa-3 text-center budget-stat-sheet">
                  <span class="text-caption font-weight-bold grey--text text--darken-3">
                    {{ money(new_budget.last_amount) }}
                  </span>
                </v-sheet>
              </v-col>
              <v-col cols="12" md="2">
                <div class="text-caption grey--text text--darken-1 mb-1">Consumido</div>
                <v-sheet rounded class="pa-3 text-center budget-stat-sheet">
                  <span class="text-subtitle-2 font-weight-bold warning--text text--darken-2">
                    {{ money(new_budget.total_consumed) }}
                  </span>
                </v-sheet>
              </v-col>
              <v-col cols="12" md="2">
                <div class="text-caption grey--text text--darken-1 mb-1">Disponible</div>
                <v-sheet rounded class="pa-3 text-center budget-stat-sheet">
                  <span class="text-subtitle-2 font-weight-bold" :class="available_budget >= 0 ? 'success--text' : 'error--text'">
                    {{ money(available_budget) }}
                  </span>
                </v-sheet>
              </v-col>
              <v-col cols="12" md="1" class="text-center">
                <div class="text-caption grey--text text--darken-1 mb-1">Uso</div>
                <v-progress-circular
                  :rotate="270"
                  :size="58"
                  :width="6"
                  :value="Math.min(newBudgetUsagePct, 100)"
                  :color="statusColorByPct(newBudgetUsagePct, available_budget)"
                >
                  <span class="text-caption font-weight-bold">{{ newBudgetUsagePct }}%</span>
                </v-progress-circular>
              </v-col>
              <v-col cols="12" md="2" class="text-center">
                <div class="text-caption grey--text text--darken-1 mb-1">Tipo</div>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      :color="new_budget.type_close ? 'error' : 'success'"
                      :outlined="!new_budget.type_close"
                      depressed
                      small
                      class="text-none rounded-lg"
                      v-bind="attrs"
                      v-on="on"
                      @click="new_budget.type_close = !new_budget.type_close"
                    >
                      <v-icon left small>{{ new_budget.type_close ? 'mdi-lock' : 'mdi-lock-open-variant' }}</v-icon>
                      {{ new_budget.type_close ? 'Cerrado' : 'Abierto' }}
                    </v-btn>
                  </template>
                  <span>Cambiar a {{ new_budget.type_close ? 'Abierto' : 'Cerrado' }}</span>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-expand-transition>

      <template v-if="loading">
        <v-row dense>
          <v-col v-for="n in 4" :key="'kpi-' + n" cols="12" sm="6" md="3">
            <v-skeleton-loader type="card" height="110"></v-skeleton-loader>
          </v-col>
        </v-row>
        <v-row dense class="mt-2">
          <v-col cols="12" md="8">
            <v-skeleton-loader type="card" height="360"></v-skeleton-loader>
          </v-col>
          <v-col cols="12" md="4">
            <v-skeleton-loader type="card" height="360"></v-skeleton-loader>
          </v-col>
        </v-row>
        <v-row dense class="mt-2">
          <v-col cols="12" md="6">
            <v-skeleton-loader type="card" height="320"></v-skeleton-loader>
          </v-col>
          <v-col cols="12" md="6">
            <v-skeleton-loader type="card" height="320"></v-skeleton-loader>
          </v-col>
        </v-row>
      </template>

      <template v-else>
        <v-alert
          v-if="!hasBudgets"
          outlined
          type="info"
          color="primary"
          class="rounded-lg"
        >
          No hay presupuestos para la combinacion seleccionada. Ajusta calendario, periodo, unidad de negocio o location para continuar.
        </v-alert>

        <template v-else>
          <v-card outlined class="rounded-xl mb-4 hero-card overflow-hidden">
            <v-card-text class="pa-4 pa-md-5">
              <v-row dense align="center">
                <v-col cols="12" md="7">
                  <div class="text-overline primary--text font-weight-bold hero-eyebrow">Resumen ejecutivo</div>
                  <div class="text-h5 font-weight-bold grey--text text--darken-4 mb-2">
                    {{ summaryTitle }}
                  </div>
                  <div class="text-body-2 grey--text text--darken-1 mb-4">
                    {{ summaryDescription }}
                  </div>
                  <div class="d-flex flex-wrap context-chips-gap">
                    <v-chip small label color="red lighten-5" class="red--text text--darken-2 font-weight-medium">
                      <v-icon x-small left color="red darken-1">mdi-alert-circle</v-icon>
                      {{ overspentCount }} sobreejecutados
                    </v-chip>
                    <v-chip small label color="orange lighten-5" class="orange--text text--darken-2 font-weight-medium">
                      <v-icon x-small left color="orange darken-2">mdi-fire</v-icon>
                      {{ criticalCount }} criticos
                    </v-chip>
                    <v-chip small label color="green lighten-5" class="green--text text--darken-2 font-weight-medium">
                      <v-icon x-small left color="green darken-1">mdi-shield-check</v-icon>
                      {{ healthyCount }} saludables
                    </v-chip>
                  </div>
                </v-col>
                <v-col cols="12" md="5">
                  <v-card flat class="hero-side-card pa-4 rounded-lg">
                    <div class="d-flex align-center justify-space-between mb-2">
                      <span class="text-subtitle-2 font-weight-bold grey--text text--darken-3">Ejecucion global</span>
                      <v-chip small label dark :color="statusColorByPct(dashboardTotals.pct, dashboardTotals.available)">
                        {{ dashboardTotals.pct }}%
                      </v-chip>
                    </div>
                    <v-progress-linear
                      :value="dashboardTotals.pct"
                      :color="statusColorByPct(dashboardTotals.pct, dashboardTotals.available)"
                      rounded
                      height="14"
                      class="mb-3"
                    ></v-progress-linear>
                    <div class="d-flex justify-space-between text-caption grey--text text--darken-1 mb-2">
                      <span>Consumido {{ money(dashboardTotals.consumed) }}</span>
                      <span>Presupuestado {{ money(dashboardTotals.budget) }}</span>
                    </div>
                    <div class="text-body-2 grey--text text--darken-2">
                      {{ primaryInsight }}
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-row dense class="mb-4">
            <v-col v-for="card in kpiCards" :key="card.title" cols="12" sm="6" md="4" lg="2">
              <v-card outlined class="rounded-lg pa-4 stat-card fill-height">
                <div class="d-flex align-center mb-3">
                  <v-avatar :color="card.avatar" size="42" class="mr-3">
                    <v-icon :color="card.color">{{ card.icon }}</v-icon>
                  </v-avatar>
                  <div class="text-caption grey--text text--darken-1 text-uppercase stat-label">
                    {{ card.title }}
                  </div>
                </div>
                <div class="text-h6 font-weight-bold" :class="card.textClass">{{ card.value }}</div>
                <div class="text-caption grey--text text--darken-1 mt-1">{{ card.subtitle }}</div>
              </v-card>
            </v-col>
          </v-row>

          <div v-if="viewMode === 'dashboard'">
            <v-row dense>
              <v-col cols="12" md="8">
                <v-card outlined class="rounded-lg overflow-hidden fill-height">
                  <v-card-title class="subtitle-1 font-weight-bold">
                    <v-icon left color="primary">mdi-chart-bar</v-icon>
                    Presupuesto vs consumido por departamento
                  </v-card-title>
                  <v-card-text>
                    <apexchart
                      v-if="departmentChart.series[0].data.length"
                      type="bar"
                      height="360"
                      :options="departmentChartOptions"
                      :series="departmentChart.series"
                    ></apexchart>
                    <div v-else class="text-center py-12 grey--text">
                      Sin datos suficientes para el grafico.
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="4">
                <v-card outlined class="rounded-lg overflow-hidden fill-height">
                  <v-card-title class="subtitle-1 font-weight-bold">
                    <v-icon left color="primary">mdi-chart-donut</v-icon>
                    Distribucion por tipo de cuenta
                  </v-card-title>
                  <v-card-text>
                    <apexchart
                      v-if="accountTypeChart.series.length"
                      type="donut"
                      height="360"
                      :options="accountTypeChartOptions"
                      :series="accountTypeChart.series"
                    ></apexchart>
                    <div v-else class="text-center py-12 grey--text">
                      Sin datos suficientes para el grafico.
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-row dense class="mt-2">
              <v-col cols="12" md="6">
                <v-card outlined class="rounded-lg overflow-hidden fill-height">
                  <v-card-title class="subtitle-1 font-weight-bold">
                    <v-icon left color="primary">mdi-trending-up</v-icon>
                    Top 10 cuentas con mayor consumo
                  </v-card-title>
                  <v-card-text>
                    <apexchart
                      v-if="topConsumedChart.series[0].data.length"
                      type="bar"
                      :height="Math.max(320, topConsumedChart.labels.length * 34)"
                      :options="topConsumedChartOptions"
                      :series="topConsumedChart.series"
                    ></apexchart>
                    <div v-else class="text-center py-12 grey--text">
                      Sin datos suficientes para el grafico.
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <v-card outlined class="rounded-lg overflow-hidden fill-height">
                  <v-card-title class="subtitle-1 font-weight-bold">
                    <v-icon left color="error">mdi-alert-octagon-outline</v-icon>
                    Top 10 desviaciones
                  </v-card-title>
                  <v-card-text>
                    <apexchart
                      v-if="topVarianceChart.series[0].data.length"
                      type="bar"
                      :height="Math.max(320, topVarianceChart.labels.length * 34)"
                      :options="topVarianceChartOptions"
                      :series="topVarianceChart.series"
                    ></apexchart>
                    <div v-else class="text-center py-12 grey--text">
                      Sin desviaciones negativas para el filtro actual.
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-row dense class="mt-2">
              <v-col cols="12" md="5">
                <v-card outlined class="rounded-lg fill-height">
                  <v-card-title class="subtitle-1 font-weight-bold">
                    <v-icon left color="primary">mdi-bell-alert-outline</v-icon>
                    Alertas y focos de atencion
                  </v-card-title>
                  <v-card-text>
                    <div v-if="priorityAlerts.length">
                      <div v-for="(alert, index) in priorityAlerts" :key="index" class="alert-item pa-3 rounded-lg mb-3">
                        <div class="d-flex align-start">
                          <v-icon small :color="alert.color" class="mr-2 mt-1">{{ alert.icon }}</v-icon>
                          <div>
                            <div class="text-body-2 font-weight-bold grey--text text--darken-3">{{ alert.title }}</div>
                            <div class="text-caption grey--text text--darken-1">{{ alert.description }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-body-2 grey--text text--darken-1">
                      No hay alertas relevantes con la combinacion actual.
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="7">
                <v-card outlined class="rounded-lg fill-height overflow-hidden">
                  <v-card-title class="subtitle-1 font-weight-bold d-flex align-center">
                    <v-icon left color="primary">mdi-table-eye</v-icon>
                    Partidas con mayor riesgo
                    <v-spacer></v-spacer>
                    <v-chip small label color="primary">{{ topRiskItems.length }}</v-chip>
                  </v-card-title>
                  <v-data-table
                    :headers="riskHeaders"
                    :items="topRiskItems"
                    dense
                    disable-pagination
                    hide-default-footer
                    class="budget-table"
                  >
                    <template v-slot:[`item.account`]="{ item }">
                      <div class="py-2">
                        <div class="font-weight-medium">{{ item.account }}</div>
                        <div class="text-caption grey--text text--darken-1">{{ item.department_display }}</div>
                      </div>
                    </template>
                    <template v-slot:[`item.total_budget`]="{ item }">
                      <span>{{ money(item.total_budget, item.currency_code) }}</span>
                    </template>
                    <template v-slot:[`item.total_consumed`]="{ item }">
                      <span class="warning--text text--darken-2 font-weight-medium">{{ money(item.total_consumed, item.currency_code) }}</span>
                    </template>
                    <template v-slot:[`item.remaining_amount`]="{ item }">
                      <span :class="item.remaining_amount >= 0 ? 'success--text font-weight-bold' : 'error--text font-weight-bold'">
                        {{ money(item.remaining_amount, item.currency_code) }}
                      </span>
                    </template>
                    <template v-slot:[`item.consumption_pct`]="{ item }">
                      <v-chip x-small dark label :color="statusColorByPct(item.consumption_pct, item.remaining_amount)">
                        {{ item.consumption_pct }}%
                      </v-chip>
                    </template>
                  </v-data-table>
                </v-card>
              </v-col>
            </v-row>
          </div>

          <v-card v-else outlined class="rounded-lg overflow-hidden">
            <div class="px-4 py-3 d-flex flex-wrap align-center grey lighten-5" style="border-bottom: 1px solid #e0e0e0; gap: 12px;">
              <span class="text-caption grey--text d-flex align-center">
                <v-icon x-small class="mr-1">mdi-cursor-default-click</v-icon>
                Usa la tabla para revisar detalle y abrir historial.
              </span>
              <v-spacer></v-spacer>
              <v-chip small label color="primary">
                {{ filteredBudgets.length }} registros
              </v-chip>
            </div>

            <v-data-table
              :headers="headers"
              :items="filteredBudgets"
              fixed-header
              height="68vh"
              :items-per-page="50"
              class="budget-table"
              :footer-props="{
                'items-per-page-text': 'Filas por pagina',
                'items-per-page-options': [25, 50, 100, -1],
              }"
            >
              <template v-slot:[`item.department_display`]="{ item }">
                <div class="py-2">
                  <div class="font-weight-medium">{{ item.department_display }}</div>
                  <div class="text-caption grey--text text--darken-1">{{ item.location_display }}</div>
                </div>
              </template>
              <template v-slot:[`item.total_budget`]="{ item }">
                <span class="font-weight-medium">{{ money(item.total_budget, item.currency_code) }}</span>
              </template>
              <template v-slot:[`item.total_consumed`]="{ item }">
                <span class="font-weight-medium warning--text text--darken-2">{{ money(item.total_consumed, item.currency_code) }}</span>
              </template>
              <template v-slot:[`item.remaining_amount`]="{ item }">
                <span class="font-weight-bold" :class="item.remaining_amount >= 0 ? 'success--text' : 'error--text'">
                  {{ money(item.remaining_amount, item.currency_code) }}
                </span>
              </template>
              <template v-slot:[`item.consumption_pct`]="{ item }">
                <div class="d-flex align-center justify-center py-2">
                  <v-progress-circular
                    :rotate="270"
                    :size="38"
                    :width="5"
                    :value="Math.min(item.consumption_pct, 100)"
                    :color="statusColorByPct(item.consumption_pct, item.remaining_amount)"
                  >
                    <span style="font-size: 9px;" class="font-weight-bold">{{ item.consumption_pct }}%</span>
                  </v-progress-circular>
                </div>
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <v-chip small dark label :color="item.status.color" class="font-weight-medium">
                  <v-icon x-small left>{{ item.status.icon }}</v-icon>
                  {{ item.status.label }}
                </v-chip>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex justify-center">
                  <v-tooltip bottom v-if="hasFeature('SHOP-012')">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small color="primary" v-bind="attrs" v-on="on" @click="editBudget(item)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Editar presupuesto</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small color="info" v-bind="attrs" v-on="on" @click="showDetails(item)">
                        <v-icon small>mdi-history</v-icon>
                      </v-btn>
                    </template>
                    <span>Ver historial</span>
                  </v-tooltip>
                </div>
              </template>
              <template v-slot:no-data>
                <div class="text-center pa-8">
                  <v-icon size="72" color="grey lighten-1">mdi-wallet-outline</v-icon>
                  <div class="text-h6 grey--text text--darken-1 mt-3">Sin resultados</div>
                  <div class="text-caption grey--text mb-2">
                    Ajusta la busqueda o el estado rapido para mostrar partidas.
                  </div>
                </div>
              </template>
            </v-data-table>
          </v-card>
        </template>
      </template>
    </template>

    <template v-else>
      <div class="d-flex flex-wrap align-center mb-4">
        <v-btn icon class="mr-3" @click="show_header = true">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-avatar color="blue-grey" size="44" class="mr-3 elevation-1">
          <v-icon dark>mdi-history</v-icon>
        </v-avatar>
        <div class="flex-grow-1 min-w-0">
          <div class="text-h5 font-weight-bold" style="line-height: 1.1;">
            Historial de presupuesto
          </div>
          <div class="text-body-2 grey--text text--darken-1 mt-1">
            {{ historyContextDescription }}
          </div>
        </div>
        <v-chip color="primary" small label class="font-weight-medium">
          <v-icon small left>mdi-counter</v-icon>
          {{ histories.length }} {{ histories.length === 1 ? 'movimiento' : 'movimientos' }}
        </v-chip>
      </div>

      <v-row dense class="mb-4">
        <v-col cols="12" md="3">
          <v-card outlined class="rounded-lg pa-4">
            <div class="text-caption grey--text text--darken-1 text-uppercase">Presupuesto</div>
            <div class="text-h6 font-weight-bold primary--text">{{ money(historyBudget.total_budget || 0, historyBudget.currency_code) }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card outlined class="rounded-lg pa-4">
            <div class="text-caption grey--text text--darken-1 text-uppercase">Consumido</div>
            <div class="text-h6 font-weight-bold warning--text text--darken-2">{{ money(historyBudget.total_consumed || 0, historyBudget.currency_code) }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card outlined class="rounded-lg pa-4">
            <div class="text-caption grey--text text--darken-1 text-uppercase">Disponible</div>
            <div class="text-h6 font-weight-bold" :class="(historyBudget.remaining_amount || 0) >= 0 ? 'success--text' : 'error--text'">
              {{ money(historyBudget.remaining_amount || 0, historyBudget.currency_code) }}
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card outlined class="rounded-lg pa-4">
            <div class="text-caption grey--text text--darken-1 text-uppercase">Uso</div>
            <div class="text-h6 font-weight-bold" :class="statusTextClass(historyBudget)">
              {{ historyBudget.consumption_pct || 0 }}%
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-card outlined class="rounded-lg overflow-hidden">
        <v-data-table
          :headers="historyHeaders"
          :items="histories"
          :items-per-page="20"
          class="budget-table"
          :footer-props="{
            'items-per-page-text': 'Filas por pagina',
            'items-per-page-options': [10, 20, 50, -1],
          }"
        >
          <template v-slot:[`item.chart_of_account_id`]="{ item }">
            <div class="d-flex align-center py-2">
              <v-avatar color="blue lighten-5" size="30" class="mr-2">
                <v-icon small color="primary">mdi-bank</v-icon>
              </v-avatar>
              <div>
                <div class="font-weight-medium text-body-2">{{ account_name(item.chart_of_account_id) }}</div>
                <div class="text-caption grey--text text--darken-1">{{ historyBudget.department_display }}</div>
              </div>
            </div>
          </template>
          <template v-slot:[`item.amount`]="{ item }">
            <v-chip
              :color="parseFloat(item.amount) >= 0 ? 'success' : 'error'"
              small
              label
              dark
              class="font-weight-bold"
            >
              <v-icon x-small left>{{ parseFloat(item.amount) >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
              {{ money(item.amount, historyBudget.currency_code) }}
            </v-chip>
          </template>
          <template v-slot:[`item.created_at`]="{ item }">
            <span class="text-body-2 grey--text text--darken-2">
              <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
              {{ get_date_format(item.created_at) }}
            </span>
          </template>
          <template v-slot:[`item.use_fullname`]="{ item }">
            <div class="d-flex align-center">
              <v-avatar color="grey lighten-3" size="26" class="mr-2">
                <v-icon small color="grey darken-1">mdi-account</v-icon>
              </v-avatar>
              <span class="text-body-2">{{ item.use_fullname }}</span>
            </div>
          </template>
          <template v-slot:[`item.running_balance`]="{ item }">
            <span class="font-weight-bold text-body-2">{{ money(item.running_balance, historyBudget.currency_code) }}</span>
          </template>
          <template v-slot:no-data>
            <div class="text-center pa-8">
              <v-icon size="64" color="grey lighten-1">mdi-history</v-icon>
              <div class="text-body-1 grey--text text--darken-1 mt-3">
                No hay movimientos registrados
              </div>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </template>
  </v-container>
</template>

<script>
import moment from 'moment'

const ALL_DEPARTMENT = { text: 'Todos los departamentos', value: 0 }
const ALL_ACCOUNTING_TYPE = { id: 0, name: 'TODAS' }
const ALL_ACCOUNT = { id: 0, name: 'TODAS' }
const ALL_CATEGORY = { id: 0, name: 'TODAS' }
const ALL_SUBCATEGORY = { id: 0, name: 'TODAS' }
const ALL_PERIODS = { id: 0, name: 'TODOS LOS PERIODOS', display_name: 'TODOS LOS PERIODOS', range_label: '' }

export default {
  name: 'ShopBudgetView',
  data: () => ({
    loading: false,
    show_header: true,
    viewMode: 'dashboard',
    quickStatus: 'all',
    search: '',
    accounts: [],
    accountingTypes: [],
    headers: [
      { text: 'Departamento', value: 'department_display' },
      { text: 'Tipo de cuenta', value: 'account_type' },
      { text: 'Cuenta', value: 'account' },
      { text: 'Categoria', value: 'category_display' },
      { text: 'Subcategoria', value: 'subcategory_display' },
      { text: 'Moneda', value: 'currency_code', align: 'center' },
      { text: 'Presupuesto', value: 'total_budget', align: 'right' },
      { text: 'Consumido', value: 'total_consumed', align: 'right' },
      { text: 'Disponible', value: 'remaining_amount', align: 'right' },
      { text: 'Uso %', value: 'consumption_pct', align: 'center', sortable: false },
      { text: 'Estado', value: 'status', align: 'center', sortable: false },
      { text: '', value: 'actions', align: 'center', sortable: false },
    ],
    riskHeaders: [
      { text: 'Partida', value: 'account' },
      { text: 'Presupuesto', value: 'total_budget', align: 'right' },
      { text: 'Consumido', value: 'total_consumed', align: 'right' },
      { text: 'Disponible', value: 'remaining_amount', align: 'right' },
      { text: 'Uso %', value: 'consumption_pct', align: 'center' },
    ],
    historyHeaders: [
      { text: 'Cuenta', value: 'chart_of_account_id' },
      { text: 'Monto', value: 'amount', align: 'right' },
      { text: 'Fecha', value: 'created_at' },
      { text: 'Responsable', value: 'use_fullname' },
      { text: 'Balance', value: 'running_balance', align: 'right' },
    ],
    quickStatusOptions: [
      { value: 'all', label: 'Todos', icon: 'mdi-view-grid-outline' },
      { value: 'overspent', label: 'Sobregiro', icon: 'mdi-alert-octagon-outline' },
      { value: 'critical', label: 'Criticos', icon: 'mdi-fire' },
      { value: 'healthy', label: 'Saludables', icon: 'mdi-shield-check-outline' },
      { value: 'no_consumption', label: 'Sin consumo', icon: 'mdi-pause-circle-outline' },
    ],
    filters: {
      bussines_unit: null,
      calendar: null,
      period: null,
      department: null,
      location: null,
      accountingType: null,
      account: null,
      category: null,
      subcategory: null,
    },
    new_budget: {
      budget_id: null,
      show: false,
      amount: 0,
      last_amount: 0,
      total_consumed: 0,
      remaining_amount: 0,
      type_close: true,
    },
    itemsDepartment: [],
    accountingBuget: [],
    bussines_units: [],
    locations: [],
    calendars: [],
    // Catalogo maestro de periodos (nombres) y los periodos ya enriquecidos con
    // el rango de fechas del calendario seleccionado.
    basePeriods: [],
    periods: [],
    histories: [],
    historyBudget: {},
    all_categories: [],
  }),
  mounted() {
    this.initialized()
  },
  computed: {
    hasBudgets() {
      return this.accountingBuget.length > 0
    },
    businessUnitsFromCalendar() {
      if (!this.filters.calendar || !this.filters.calendar.business_units) {
        return []
      }
      return this.filters.calendar.business_units
        .map((item) => this.bussines_units.find((element) => element.id === item.business_unit_id))
        .filter(Boolean)
    },
    businessLocations() {
      if (!this.filters.bussines_unit) {
        return []
      }
      const locations = this.locations.filter(
        (element) => element.bus_id === this.filters.bussines_unit.id
      )
      return [
        { text: 'Todas las locations', value: 0, bus_id: this.filters.bussines_unit.id },
        ...locations,
      ]
    },
    departmentOptions() {
      return this.itemsDepartment.length ? this.itemsDepartment : [ALL_DEPARTMENT]
    },
    accountingTypeOptions() {
      return this.accountingTypes.length ? this.accountingTypes : [ALL_ACCOUNTING_TYPE]
    },
    accountOptions() {
      let accounts = [...this.accounts]
      if (this.filters.accountingType && this.filters.accountingType.id > 0) {
        accounts = accounts.filter((item) => item.account_type_id === this.filters.accountingType.id)
      }
      return [ALL_ACCOUNT, ...accounts]
    },
    categoryOptions() {
      const parents = this.all_categories.filter((element) => element.parent_id == null)
      return [ALL_CATEGORY, ...parents]
    },
    subcategoryOptions() {
      if (!this.filters.category || this.filters.category.id === 0) {
        return [ALL_SUBCATEGORY]
      }
      const children = this.all_categories.filter(
        (element) => element.parent_id === this.filters.category.id
      )
      return [ALL_SUBCATEGORY, ...children]
    },
    normalizedBudgets() {
      let data = [...this.accountingBuget]

      // Si se seleccionan todos los periodos, agrupamos por dimensiones clave
      if (this.filters.period && this.filters.period.id === 0) {
        const grouped = {}
        data.forEach((item) => {
          const key = `${item.chart_of_account_id}-${item.department_id}-${item.location_id}-${item.category_id || 0}-${item.subcategory_id || 0}`
          if (!grouped[key]) {
            grouped[key] = { ...item, total_budget: 0, total_consumed: 0, remaining_amount: 0 }
          }
          grouped[key].total_budget += parseFloat(item.total_budget || 0)
          grouped[key].total_consumed += parseFloat(item.total_consumed || 0)
          grouped[key].remaining_amount += parseFloat(item.remaining_amount || 0)
        })
        data = Object.values(grouped)
      }

      return data.map((item) => {
        const total_budget = parseFloat(item.total_budget || 0)
        const total_consumed = parseFloat(item.total_consumed || 0)
        const remaining_amount = parseFloat(item.remaining_amount || 0)
        const consumption_pct = total_budget > 0
          ? parseFloat(((total_consumed * 100) / total_budget).toFixed(1))
          : 0
        const department_display = this.department_name(item.department_id)
        const location_display = this.location_name(item.location_id)
        const category_display = item.category || 'Sin categoria'
        const subcategory_display = item.subcategory || 'Sin subcategoria'
        const status = this.getStatusMeta({ consumption_pct, remaining_amount, total_consumed })
        return {
          ...item,
          total_budget,
          total_consumed,
          remaining_amount,
          consumption_pct,
          department_display,
          location_display,
          category_display,
          subcategory_display,
          status,
        }
      })
    },
    filteredBudgets() {
      let items = [...this.normalizedBudgets]

      if (this.filters.bussines_unit) {
        items = items.filter((item) => item.business_unit_id === this.filters.bussines_unit.id)
      }
      if (this.filters.location && this.filters.location.value !== 0) {
        items = items.filter((item) => item.location_id === this.filters.location.value)
      }
      if (this.filters.department && this.filters.department.value !== 0) {
        items = items.filter((item) => item.department_id === this.filters.department.value)
      }
      if (this.filters.accountingType && this.filters.accountingType.id !== 0) {
        items = items.filter((item) => item.account_type_id === this.filters.accountingType.id)
      }
      if (this.filters.account && this.filters.account.id !== 0) {
        items = items.filter((item) => item.chart_of_account_id === this.filters.account.id)
      }
      if (this.filters.category && this.filters.category.id !== 0) {
        items = items.filter((item) => item.category_id === this.filters.category.id)
      }
      if (this.filters.subcategory && this.filters.subcategory.id !== 0) {
        items = items.filter((item) => item.subcategory_id === this.filters.subcategory.id)
      }

      const term = (this.search || '').trim().toLowerCase()
      if (term) {
        items = items.filter((item) => {
          return [
            item.account,
            item.account_type,
            item.category_display,
            item.subcategory_display,
            item.department_display,
            item.location_display,
            item.currency_code,
          ]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(term))
        })
      }

      if (this.quickStatus === 'overspent') {
        items = items.filter((item) => item.remaining_amount < 0)
      } else if (this.quickStatus === 'critical') {
        items = items.filter((item) => item.remaining_amount >= 0 && item.consumption_pct >= 80)
      } else if (this.quickStatus === 'healthy') {
        items = items.filter((item) => item.remaining_amount >= 0 && item.consumption_pct < 80 && item.total_consumed > 0)
      } else if (this.quickStatus === 'no_consumption') {
        items = items.filter((item) => item.total_consumed === 0)
      }

      return items.sort((a, b) => {
        if (a.remaining_amount !== b.remaining_amount) {
          return a.remaining_amount - b.remaining_amount
        }
        return b.total_consumed - a.total_consumed
      })
    },
    dashboardTotals() {
      const budget = this.filteredBudgets.reduce((sum, item) => sum + item.total_budget, 0)
      const consumed = this.filteredBudgets.reduce((sum, item) => sum + item.total_consumed, 0)
      const available = this.filteredBudgets.reduce((sum, item) => sum + item.remaining_amount, 0)
      const pct = budget > 0 ? parseFloat(((consumed * 100) / budget).toFixed(1)) : 0
      return { budget, consumed, available, pct }
    },
    overspentCount() {
      return this.filteredBudgets.filter((item) => item.remaining_amount < 0).length
    },
    criticalCount() {
      return this.filteredBudgets.filter((item) => item.remaining_amount >= 0 && item.consumption_pct >= 80).length
    },
    healthyCount() {
      return this.filteredBudgets.filter((item) => item.remaining_amount >= 0 && item.consumption_pct < 80 && item.total_consumed > 0).length
    },
    noConsumptionCount() {
      return this.filteredBudgets.filter((item) => item.total_consumed === 0).length
    },
    kpiCards() {
      return [
        {
          title: 'Partidas',
          value: this.filteredBudgets.length,
          subtitle: 'Registros visibles con el filtro actual',
          icon: 'mdi-counter',
          color: 'primary',
          avatar: 'primary lighten-5',
          textClass: 'primary--text',
        },
        {
          title: 'Presupuestado',
          value: this.money(this.dashboardTotals.budget),
          subtitle: 'Monto total configurado',
          icon: 'mdi-cash-multiple',
          color: 'blue',
          avatar: 'blue lighten-5',
          textClass: 'blue--text text--darken-2',
        },
        {
          title: 'Consumido',
          value: this.money(this.dashboardTotals.consumed),
          subtitle: 'Ejecucion acumulada del periodo',
          icon: 'mdi-cart-outline',
          color: 'orange',
          avatar: 'orange lighten-5',
          textClass: 'orange--text text--darken-2',
        },
        {
          title: 'Disponible',
          value: this.money(this.dashboardTotals.available),
          subtitle: this.dashboardTotals.available >= 0 ? 'Saldo restante' : 'Deficit acumulado',
          icon: 'mdi-cash-check',
          color: this.dashboardTotals.available >= 0 ? 'green' : 'red',
          avatar: this.dashboardTotals.available >= 0 ? 'green lighten-5' : 'red lighten-5',
          textClass: this.dashboardTotals.available >= 0 ? 'green--text text--darken-2' : 'error--text',
        },
        {
          title: 'Sobregiro',
          value: this.overspentCount,
          subtitle: 'Partidas con saldo negativo',
          icon: 'mdi-alert-octagon-outline',
          color: 'red',
          avatar: 'red lighten-5',
          textClass: 'error--text',
        },
        {
          title: 'Sin consumo',
          value: this.noConsumptionCount,
          subtitle: 'Partidas sin ejecucion',
          icon: 'mdi-pause-circle-outline',
          color: 'grey',
          avatar: 'blue-grey lighten-5',
          textClass: 'blue-grey--text text--darken-1',
        },
      ]
    },
    departmentSummary() {
      const grouped = {}
      this.filteredBudgets.forEach((item) => {
        const key = item.department_display
        if (!grouped[key]) {
          grouped[key] = {
            label: key,
            budget: 0,
            consumed: 0,
            available: 0,
          }
        }
        grouped[key].budget += item.total_budget
        grouped[key].consumed += item.total_consumed
        grouped[key].available += item.remaining_amount
      })
      return Object.values(grouped)
        .map((item) => ({
          ...item,
          pct: item.budget > 0 ? parseFloat(((item.consumed * 100) / item.budget).toFixed(1)) : 0,
        }))
        .sort((a, b) => b.budget - a.budget)
    },
    departmentChart() {
      const items = this.departmentSummary.slice(0, 10)
      return {
        labels: items.map((item) => this.shortLabel(item.label, 24)),
        series: [
          { name: 'Presupuesto', data: items.map((item) => parseFloat(item.budget.toFixed(2))) },
          { name: 'Consumido', data: items.map((item) => parseFloat(item.consumed.toFixed(2))) },
        ],
      }
    },
    departmentChartOptions() {
      return {
        chart: { toolbar: { show: true }, animations: { enabled: true, speed: 600 } },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '48%' } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.departmentChart.labels,
          labels: { rotate: -35, trim: true, style: { fontSize: '11px' } },
        },
        yaxis: { labels: { formatter: (value) => this.axisCurrency(value) } },
        colors: ['#1E88E5', '#FB8C00'],
        legend: { position: 'top' },
        tooltip: { y: { formatter: (value) => this.money(value) } },
        grid: { borderColor: '#eceff1', strokeDashArray: 4 },
      }
    },
    accountTypeChart() {
      const grouped = {}
      this.filteredBudgets.forEach((item) => {
        const key = item.account_type || 'Sin tipo'
        if (!grouped[key]) {
          grouped[key] = 0
        }
        grouped[key] += item.total_budget
      })
      return {
        labels: Object.keys(grouped),
        series: Object.values(grouped).map((value) => parseFloat(value.toFixed(2))),
      }
    },
    accountTypeChartOptions() {
      return {
        labels: this.accountTypeChart.labels,
        chart: { animations: { enabled: true, speed: 600 } },
        colors: ['#1E88E5', '#43A047', '#FB8C00', '#8E24AA', '#00897B', '#5E35B1', '#6D4C41'],
        legend: { position: 'bottom', fontSize: '12px' },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (value) => this.money(value) } },
        plotOptions: {
          pie: {
            donut: {
              size: '60%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total',
                  formatter: (w) => this.money(
                    w.globals.seriesTotals.reduce((sum, value) => sum + value, 0)
                  ),
                },
              },
            },
          },
        },
      }
    },
    topConsumedChart() {
      const items = [...this.filteredBudgets]
        .sort((a, b) => b.total_consumed - a.total_consumed)
        .slice(0, 10)
      return {
        labels: items.map((item) => this.shortLabel(item.account || 'Sin cuenta', 30)),
        series: [{ name: 'Consumido', data: items.map((item) => parseFloat(item.total_consumed.toFixed(2))) }],
      }
    },
    topConsumedChartOptions() {
      return {
        chart: { toolbar: { show: true }, animations: { enabled: true, speed: 600 } },
        plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%' } },
        dataLabels: { enabled: true, formatter: (value) => this.money(value) },
        xaxis: {
          categories: this.topConsumedChart.labels,
          labels: { formatter: (value) => this.axisCurrency(value) },
        },
        colors: ['#FB8C00'],
        legend: { show: false },
        tooltip: { y: { formatter: (value) => this.money(value) } },
        grid: { borderColor: '#eceff1', strokeDashArray: 4 },
      }
    },
    topVarianceChart() {
      const items = [...this.filteredBudgets]
        .filter((item) => item.remaining_amount < 0)
        .sort((a, b) => a.remaining_amount - b.remaining_amount)
        .slice(0, 10)
      return {
        labels: items.map((item) => this.shortLabel(item.account || 'Sin cuenta', 30)),
        series: [{
          name: 'Desviacion',
          data: items.map((item) => parseFloat(Math.abs(item.remaining_amount).toFixed(2))),
        }],
      }
    },
    topVarianceChartOptions() {
      return {
        chart: { toolbar: { show: true }, animations: { enabled: true, speed: 600 } },
        plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%' } },
        dataLabels: { enabled: true, formatter: (value) => this.money(value) },
        xaxis: {
          categories: this.topVarianceChart.labels,
          labels: { formatter: (value) => this.axisCurrency(value) },
        },
        colors: ['#E53935'],
        legend: { show: false },
        tooltip: { y: { formatter: (value) => this.money(value) } },
        grid: { borderColor: '#eceff1', strokeDashArray: 4 },
      }
    },
    topRiskItems() {
      return [...this.filteredBudgets]
        .sort((a, b) => {
          if (a.remaining_amount < 0 && b.remaining_amount >= 0) return -1
          if (a.remaining_amount >= 0 && b.remaining_amount < 0) return 1
          if (a.consumption_pct !== b.consumption_pct) return b.consumption_pct - a.consumption_pct
          return a.remaining_amount - b.remaining_amount
        })
        .slice(0, 8)
    },
    priorityAlerts() {
      const alerts = []
      if (this.overspentCount > 0) {
        const item = this.filteredBudgets.find((budget) => budget.remaining_amount < 0)
        alerts.push({
          color: 'error',
          icon: 'mdi-alert-circle',
          title: `${this.overspentCount} partidas con sobregiro`,
          description: item
            ? `La cuenta ${item.account} en ${item.department_display} ya supera el presupuesto disponible.`
            : 'Existen partidas con saldo negativo que requieren ajuste inmediato.',
        })
      }
      if (this.criticalCount > 0) {
        alerts.push({
          color: 'orange darken-2',
          icon: 'mdi-fire',
          title: `${this.criticalCount} partidas por encima del 80%`,
          description: 'Conviene revisar consumo y compromisos pendientes antes del cierre del periodo.',
        })
      }
      if (this.departmentSummary.length > 0) {
        const leader = this.departmentSummary[0]
        alerts.push({
          color: 'primary',
          icon: 'mdi-office-building',
          title: `Mayor peso presupuestario: ${leader.label}`,
          description: `${this.money(leader.budget)} asignados con ejecucion de ${leader.pct}% para el filtro actual.`,
        })
      }
      if (this.noConsumptionCount > 0) {
        alerts.push({
          color: 'blue-grey darken-1',
          icon: 'mdi-pause-circle-outline',
          title: `${this.noConsumptionCount} partidas aun sin consumo`,
          description: 'Pueden representar reserva, retraso operativo o una oportunidad de reasignacion.',
        })
      }
      return alerts.slice(0, 4)
    },
    activeFilterChips() {
      const chips = []
      if (this.filters.location && this.filters.location.value !== 0) {
        chips.push({ key: 'location', label: `Location: ${this.filters.location.text}` })
      }
      if (this.filters.department && this.filters.department.value !== 0) {
        chips.push({ key: 'department', label: `Departamento: ${this.filters.department.text}` })
      }
      if (this.filters.accountingType && this.filters.accountingType.id !== 0) {
        chips.push({ key: 'accountingType', label: `Tipo: ${this.filters.accountingType.name}` })
      }
      if (this.filters.account && this.filters.account.id !== 0) {
        chips.push({ key: 'account', label: `Cuenta: ${this.filters.account.name}` })
      }
      if (this.filters.category && this.filters.category.id !== 0) {
        chips.push({ key: 'category', label: `Categoria: ${this.filters.category.name}` })
      }
      if (this.filters.subcategory && this.filters.subcategory.id !== 0) {
        chips.push({ key: 'subcategory', label: `Subcategoria: ${this.filters.subcategory.name}` })
      }
      if (this.quickStatus !== 'all') {
        const option = this.quickStatusOptions.find((item) => item.value === this.quickStatus)
        if (option) {
          chips.push({ key: 'quickStatus', label: `Vista: ${option.label}` })
        }
      }
      if (this.search) {
        chips.push({ key: 'search', label: `Busqueda: ${this.search}` })
      }
      return chips
    },
    summaryTitle() {
      return `${this.selectedBusinessName} · ${this.selectedPeriodName}`
    },
    summaryDescription() {
      const location = this.selectedLocationName
      if (location === 'Todas las locations') {
        return 'Vista consolidada de la unidad de negocio para el periodo seleccionado, con foco en desvio, riesgo y capacidad disponible.'
      }
      return `Vista de ejecucion presupuestaria concentrada en ${location}, lista para analizar desviaciones y cuentas criticas.`
    },
    primaryInsight() {
      if (this.dashboardTotals.available < 0) {
        return 'El consolidado ya presenta deficit. Prioriza revisar partidas sobreejecutadas y posibles reasignaciones.'
      }
      if (this.dashboardTotals.pct >= 85) {
        return 'El consumo global se acerca al limite. Conviene controlar compromisos pendientes y cuentas de mayor presion.'
      }
      if (this.noConsumptionCount > 0) {
        return 'Existen partidas sin movimiento. Revisa si deben mantenerse como reserva o reasignarse.'
      }
      return 'La ejecucion se mantiene controlada para el filtro actual, con espacio para seguimiento por departamento y cuenta.'
    },
    selectedBusinessName() {
      return this.filters.bussines_unit ? this.filters.bussines_unit.description_long : 'Sin unidad de negocio'
    },
    selectedPeriodName() {
      const calendar = this.filters.calendar ? this.filters.calendar.name : 'Sin calendario'
      const period = this.filters.period ? this.filters.period.name : 'Sin periodo'
      const range = this.filters.period ? this.filters.period.range_label : ''
      return range ? `${calendar} / ${period} (${range})` : `${calendar} / ${period}`
    },
    // Moneda base de la unidad de negocio seleccionada. Todas las partidas de
    // la vista pertenecen a esa unidad, asi que la moneda es una sola.
    currencyCode() {
      if (this.filters.bussines_unit && this.filters.bussines_unit.currency_code) {
        return this.filters.bussines_unit.currency_code
      }
      const row = this.accountingBuget.find((item) => item.currency_code)
      return row ? row.currency_code : ''
    },
    selectedLocationName() {
      return this.filters.location ? this.filters.location.text : 'Todas las locations'
    },
    budgetSelectionLabel() {
      const department = this.filters.department && this.filters.department.value !== 0
        ? this.filters.department.text
        : 'Sin departamento'
      const account = this.filters.account && this.filters.account.id !== 0
        ? this.filters.account.name
        : 'Sin cuenta'
      return `${department} · ${account}`
    },
    newBudgetUsagePct() {
      const baseBudget = parseFloat(this.new_budget.last_amount || 0)
      const consumed = parseFloat(this.new_budget.total_consumed || 0)
      const updatedBudget = baseBudget + parseFloat(this.new_budget.amount || 0)
      if (updatedBudget <= 0) {
        return 0
      }
      return parseFloat(((consumed * 100) / updatedBudget).toFixed(1))
    },
    available_budget() {
      return parseFloat(this.new_budget.remaining_amount || 0) + parseFloat(this.new_budget.amount || 0)
    },
    historyContextDescription() {
      if (!this.historyBudget || !this.historyBudget.account) {
        return 'Movimientos y ajustes realizados al presupuesto seleccionado.'
      }
      return `${this.historyBudget.account} · ${this.historyBudget.department_display} · ${this.historyBudget.location_display}`
    },
    totalAmountProxy: {
      get() {
        return (parseFloat(this.new_budget.last_amount || 0) + parseFloat(this.new_budget.amount || 0)).toFixed(2)
      },
      set(val) {
        this.new_budget.amount = (parseFloat(val || 0) - parseFloat(this.new_budget.last_amount || 0)).toFixed(2)
      },
    },
  },
  watch: {
    'filters.category': function () {
      if (!this.filters.category || this.filters.category.id === 0) {
        this.filters.subcategory = ALL_SUBCATEGORY
      } else if (!this.subcategoryOptions.find((item) => item.id === (this.filters.subcategory && this.filters.subcategory.id))) {
        this.filters.subcategory = this.subcategoryOptions[0]
      }
      this.find_budget()
    },
    'filters.account': function () {
      this.find_budget()
    },
    'filters.subcategory': function () {
      this.find_budget()
    },
    'filters.department': function () {
      this.find_budget()
    },
  },
  methods: {
    async initialized() {
      this.loading = true
      try {
        const [calendars, periods, types, accounts, categories] = await Promise.all([
          this.api_Get('accounting/get_calendars'),
          this.api_Get('accounting/accounting_periods'),
          this.api_Get('accounting/accounting_account'),
          this.api_Get('accounting/accountsUsable'),
          this.api_Get('accounting/categories_diary_book'),
        ])

        this.calendars = await calendars.json()
        this.basePeriods = await periods.json()
        this.periods = [ALL_PERIODS, ...this.basePeriods]
        this.accountingTypes = [ALL_ACCOUNTING_TYPE, ...(await types.json())]
        this.accounts = await accounts.json()
        this.all_categories = await categories.json()

        this.filters.calendar = this.calendars[0] || null
        await this.loadPeriodRanges()
        this.filters.period = this.periods[1] || this.periods[0] || null
        this.filters.accountingType = this.accountingTypes.find((t) => t.id === 6) || this.accountingTypes[0]
        this.filters.account = ALL_ACCOUNT
        this.filters.category = ALL_CATEGORY
        this.filters.subcategory = ALL_SUBCATEGORY

        const [businessUnits, locations, departments] = await Promise.all([
          this.g_bussines(),
          this.g_locations(),
          this.g_departments(),
        ])

        this.bussines_units = businessUnits
        this.locations = locations
        this.itemsDepartment = [ALL_DEPARTMENT, ...departments]
        this.filters.department = this.itemsDepartment[0]

        this.syncBusinessSelection()
        await this.get_budget()
      } catch (error) {
        console.error(error.message)
        this.$toast.error('No se pudo cargar la informacion de presupuestos')
      } finally {
        this.loading = false
      }
    },
    syncBusinessSelection() {
      if (!this.businessUnitsFromCalendar.length) {
        this.filters.bussines_unit = null
        this.filters.location = null
        return
      }
      const currentId = this.filters.bussines_unit ? this.filters.bussines_unit.id : null
      this.filters.bussines_unit = this.businessUnitsFromCalendar.find((item) => item.id === currentId) || this.businessUnitsFromCalendar[0]
      this.syncLocationSelection()
    },
    syncLocationSelection() {
      const locations = this.businessLocations
      if (!locations.length) {
        this.filters.location = null
        return
      }
      const currentValue = this.filters.location ? this.filters.location.value : null
      this.filters.location = locations.find((item) => item.value === currentValue) || locations[0]
    },
    // El rango de fechas de un periodo depende del calendario, asi que se
    // recarga cada vez que cambia. Si un periodo no esta definido en el
    // calendario seleccionado se deja solo con su nombre.
    async loadPeriodRanges() {
      const rebuild = (rangesById) => {
        this.periods = [
          ALL_PERIODS,
          ...this.basePeriods.map((period) => {
            const range = rangesById[period.id]
            const range_label = range
              ? `${moment(range.date_from).format('DD MMM YYYY')} - ${moment(range.date_to).format('DD MMM YYYY')}`
              : ''
            return {
              ...period,
              date_from: range ? range.date_from : null,
              date_to: range ? range.date_to : null,
              range_label,
              display_name: range_label ? `${period.name} (${range_label})` : period.name,
            }
          }),
        ]
        // filters.period apunta al objeto viejo: se reemplaza por el nuevo.
        const currentId = this.filters.period ? this.filters.period.id : null
        if (currentId !== null) {
          this.filters.period = this.periods.find((item) => item.id === currentId) || this.periods[0]
        }
      }

      if (!this.filters.calendar) {
        rebuild({})
        return
      }
      try {
        const response = await this.api_Get(`accounting/periods_by_calendar/${this.filters.calendar.id}`)
        const data = await response.json()
        const rangesById = {}
        for (const row of data) {
          rangesById[row.accounting_period_id] = { date_from: row.date_from, date_to: row.date_to }
        }
        rebuild(rangesById)
      } catch (error) {
        console.error(error.message)
        rebuild({})
      }
    },
    async handleCalendarChange() {
      await this.loadPeriodRanges()
      this.syncBusinessSelection()
      this.refreshData()
    },
    handleBusinessChange() {
      this.syncLocationSelection()
      this.refreshData()
    },
    handleAccountingTypeChange() {
      if (!this.accountOptions.find((item) => item.id === (this.filters.account && this.filters.account.id))) {
        this.filters.account = this.accountOptions[0]
      }
      this.find_budget()
    },
    handleCategoryChange() {
      if (!this.subcategoryOptions.find((item) => item.id === (this.filters.subcategory && this.filters.subcategory.id))) {
        this.filters.subcategory = this.subcategoryOptions[0]
      }
      this.find_budget()
    },
    resetAnalysisFilters() {
      this.quickStatus = 'all'
      this.search = ''
      this.filters.location = this.businessLocations[0] || null
      this.filters.department = this.departmentOptions[0]
      this.filters.accountingType = this.accountingTypeOptions[0]
      this.filters.account = this.accountOptions[0]
      this.filters.category = this.categoryOptions[0]
      this.filters.subcategory = this.subcategoryOptions[0]
      this.new_budget.show = false
      this.find_budget()
      this.refreshData()
    },
    async refreshData() {
      await this.get_budget()
      this.find_budget()
    },
    editBudget(item) {
      // Sincronizar filtros con el item seleccionado
      if (item.location_id) {
        const loc = this.businessLocations.find(l => l.value === item.location_id)
        if (loc) this.filters.location = loc
      }
      if (item.department_id) {
        const dept = this.itemsDepartment.find(d => d.value === item.department_id)
        if (dept) this.filters.department = dept
      }
      if (item.account_type_id) {
        const type = this.accountingTypes.find(t => t.id === item.account_type_id)
        if (type) this.filters.accountingType = type
      }
      if (item.chart_of_account_id) {
        const acc = this.accountOptions.find(a => a.id === item.chart_of_account_id)
        if (acc) this.filters.account = acc
      }
      if (item.category_id) {
        const cat = this.all_categories.find(c => c.id === item.category_id)
        if (cat) this.filters.category = cat
      } else {
        this.filters.category = ALL_CATEGORY
      }
      if (item.subcategory_id) {
        const sub = this.all_categories.find(s => s.id === item.subcategory_id)
        if (sub) this.filters.subcategory = sub
      } else {
        this.filters.subcategory = ALL_SUBCATEGORY
      }

      this.find_budget()
      this.new_budget.amount = 0 // Reiniciar ajuste al editar
      this.new_budget.show = true
      
      // Hacer scroll hacia arriba para ver el formulario
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    add() {
      if (!this.new_budget.show) {
        if (!this.validateBudgetSelection()) {
          return
        }
        this.find_budget()
        this.new_budget.show = true
        return
      }
      this.add_budget()
    },
    validateBudgetSelection() {
      if (!this.filters.bussines_unit) {
        this.$toast.warning('Debe seleccionar la unidad de negocio')
        return false
      }
      if (!this.filters.location || this.filters.location.value === 0) {
        this.$toast.warning('Debe seleccionar una location especifica para registrar el presupuesto')
        return false
      }
      if (!this.filters.department || this.filters.department.value === 0) {
        this.$toast.warning('Debe seleccionar un departamento')
        return false
      }
      if (!this.filters.account || this.filters.account.id === 0) {
        this.$toast.warning('Debe seleccionar una cuenta')
        return false
      }
      return true
    },
    find_budget() {
      if (!this.filters.account || !this.filters.department || !this.filters.location) {
        return
      }
      const categoryId = this.filters.category && this.filters.category.id !== 0 ? this.filters.category.id : null
      const subcategoryId = this.filters.subcategory && this.filters.subcategory.id !== 0 ? this.filters.subcategory.id : null
      const budget = this.normalizedBudgets.find((element) => {
        return element.location_id === this.filters.location.value &&
          element.department_id === this.filters.department.value &&
          element.chart_of_account_id === this.filters.account.id &&
          element.category_id === categoryId &&
          element.subcategory_id === subcategoryId
      })
      if (budget) {
        this.new_budget.budget_id = budget.budget_id
        this.new_budget.total_consumed = budget.total_consumed
        this.new_budget.remaining_amount = budget.remaining_amount
        this.new_budget.last_amount = budget.total_budget
        this.new_budget.type_close = budget.accounting_budget_type_id === 1
        return
      }
      this.new_budget.budget_id = null
      this.new_budget.total_consumed = 0
      this.new_budget.last_amount = 0
      this.new_budget.remaining_amount = 0
      this.new_budget.type_close = true
    },
    async get_budget() {
      if (!this.filters.calendar || !this.filters.period || !this.filters.bussines_unit) {
        this.accountingBuget = []
        return
      }
      this.loading = true
      try {
        const filter = {
          calendar_id: this.filters.calendar.id,
          period_id: this.filters.period.id,
          location_id: this.filters.location ? this.filters.location.value : 0,
          business_unit_id: this.filters.bussines_unit.id,
        }
        const accountingBuget = await this.api_Post('accounting/accounting_buget_department', filter)
        this.accountingBuget = await accountingBuget.json()
      } catch (error) {
        console.error(error.message)
        this.accountingBuget = []
        this.$toast.error('Error al cargar los presupuestos')
      } finally {
        this.loading = false
      }
    },
    async add_budget() {
      if (!this.validateBudgetSelection()) {
        return
      }
      if (parseFloat(this.new_budget.amount || 0) === 0) {
        this.$toast.warning('El monto no puede ser 0')
        return
      }
      if (parseFloat(this.new_budget.amount || 0) + parseFloat(this.new_budget.remaining_amount || 0) < 0) {
        this.$toast.warning('Monto no valido')
        return
      }
      const details = [{ amount: this.new_budget.amount }]
      try {
        const data = {
          accounting_budget_id: this.new_budget.budget_id,
          business_unit_id: this.filters.bussines_unit.id,
          location_id: this.filters.location.value,
          accounting_calendar_id: this.filters.calendar.id,
          accounting_period_id: this.filters.period.id,
          accounting_type_id: this.new_budget.type_close ? 1 : 2,
          department_id: this.filters.department.value,
          chart_of_account_id: this.filters.account.id,
          category_id: this.filters.category && this.filters.category.id !== 0 ? this.filters.category.id : null,
          subcategory_id: this.filters.subcategory && this.filters.subcategory.id !== 0 ? this.filters.subcategory.id : null,
          created_by: this.$store.state.auth.user.user.user_id,
          details,
        }
        const accountingBuget = await this.api_Post('accounting/add_accounting_buget', data)
        if (accountingBuget.status !== 200) {
          this.$toast.error('Error al intentar registrar el presupuesto')
          return
        }
        this.$toast.success('Presupuesto registrado correctamente')
        this.new_budget.amount = 0
        this.new_budget.show = false
        await this.refreshData()
      } catch (error) {
        console.error(error.message)
      }
    },
    getStatusMeta(item) {
      if (item.remaining_amount < 0) {
        return { label: 'Sobregiro', color: 'error', icon: 'mdi-alert-octagon' }
      }
      if (item.total_consumed === 0) {
        return { label: 'Sin consumo', color: 'blue-grey', icon: 'mdi-pause-circle' }
      }
      if (item.consumption_pct >= 90) {
        return { label: 'Critico', color: 'error', icon: 'mdi-fire' }
      }
      if (item.consumption_pct >= 75) {
        return { label: 'En alerta', color: 'warning', icon: 'mdi-alert' }
      }
      return { label: 'Saludable', color: 'success', icon: 'mdi-check-circle' }
    },
    statusColorByPct(pct, remaining) {
      if (remaining < 0) {
        return 'error'
      }
      if (pct >= 90) {
        return 'error'
      }
      if (pct >= 75) {
        return 'warning'
      }
      return 'success'
    },
    statusTextClass(item) {
      return this.statusColorByPct(item.consumption_pct || 0, item.remaining_amount || 0) + '--text'
    },
    // Monto con el codigo de moneda al lado. `code` permite usar la moneda de
    // la fila cuando existe, en vez de la de la unidad seleccionada.
    money(value, code) {
      const currency = code || this.currencyCode
      const amount = this.currencyFormat(value)
      return currency ? `${currency} ${amount}` : amount
    },
    axisCurrency(value) {
      if (Math.abs(value) >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M`
      }
      if (Math.abs(value) >= 1000) {
        return `${(value / 1000).toFixed(0)}k`
      }
      return Number(value).toFixed(0)
    },
    shortLabel(value, max) {
      if (!value) {
        return '-'
      }
      return value.length > max ? `${value.substring(0, max)}...` : value
    },
    async showDetails(item) {
      item.loading = true
      try {
        const accountingBuget = await this.api_Post('accounting/accounting_buget_histories', { id: item.budget_id })
        if (accountingBuget.status !== 200) {
          this.$toast.error('Error al cargar el historico')
          return
        }
        const histories = await accountingBuget.json()
        let runningBalance = 0
        this.histories = histories.map((history) => {
          runningBalance += parseFloat(history.amount || 0)
          return {
            ...history,
            running_balance: runningBalance,
          }
        })
        this.historyBudget = item
        this.show_header = false
      } catch (error) {
        console.error(error.message)
      } finally {
        item.loading = false
      }
    },
    get_date_format(value) {
      return moment(value).format('DD/MM/YYYY HH:mm')
    },
    account_name(chart_of_account_id) {
      const account = this.accounts.find((element) => element.id === chart_of_account_id)
      return account ? account.name : '-'
    },
    department_name(department_id) {
      const department = this.itemsDepartment.find((element) => element.value === department_id)
      return department ? department.text : `Departamento ${department_id}`
    },
    location_name(location_id) {
      const location = this.locations.find((element) => element.value === location_id)
      return location ? location.text : 'Sin location'
    },
  },
}
</script>

<style scoped>
.budget-page {
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.9) 0%, rgba(255, 255, 255, 1) 35%);
}

.budget-header-gap {
  gap: 16px;
}

.header-actions-gap {
  gap: 10px;
}

.context-chips-gap {
  gap: 8px;
}

.filters-title-gap {
  gap: 12px;
}

.new-budget-gap {
  gap: 12px;
}

.min-w-0 {
  min-width: 0;
}

.section-label {
  letter-spacing: 0.5px;
}

.budget-filters-card {
  position: relative;
  transition: box-shadow 0.2s, transform 0.2s;
}

.budget-filters-card:hover {
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08) !important;
  transform: translateY(-1px);
}

.filters-accent {
  height: 4px;
  background: linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #66bb6a 100%);
}

.budget-new-header {
  background: linear-gradient(135deg, #2e7d32 0%, #43a047 100%);
}

.budget-stat-sheet {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
}

.hero-card {
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(66, 165, 245, 0.03) 45%, rgba(76, 175, 80, 0.06) 100%);
  border-color: rgba(25, 118, 210, 0.18) !important;
}

.hero-eyebrow {
  letter-spacing: 1px;
}

.hero-side-card {
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(25, 118, 210, 0.12);
}

.quick-toggle {
  flex-wrap: wrap;
}

.alert-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.stat-card {
  transition: box-shadow 0.2s, transform 0.2s;
}

.stat-card:hover {
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.1) !important;
  transform: translateY(-2px);
}

.stat-label {
  letter-spacing: 0.4px;
}

.fill-height {
  height: 100%;
}

.budget-table >>> .v-data-table-header th {
  font-size: 0.75rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #546e7a !important;
  white-space: nowrap;
}

.budget-table >>> tbody tr {
  transition: background-color 0.15s;
}

.budget-table >>> tbody tr:hover {
  background-color: #eff6ff !important;
}

.budget-table >>> tbody td {
  font-size: 0.84rem !important;
}

@media (max-width: 960px) {
  .header-actions-gap,
  .budget-header-gap {
    width: 100%;
  }
}
</style>
